import { useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useSaveFlow } from '../../components/confirm';
import { useQueryClient } from '@tanstack/react-query';
import type { NotifyApi } from '../../components/notifications';
import '../accounting/accounting.css';
import { CountryStatesPanel } from '../settings/systemManagement/CountryStatesPanel';
import { useCountries, useSaveCountry, errMessage, type Country } from '../settings/systemManagement/systemMasters';
import type { StagedState } from '../settings/systemManagement/city';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "Country" create/edit popup, opened from the Country edit (✎) icon in
// the New Contact form. REUSES the Settings ▸ System Management Country editor
// verbatim — English Long/Short Name + National Flag + the nested
// <CountryStatesPanel> which brings the FULL Country → State → City → Area chain
// with New / Link / Unlink / Edit / Export at every level (exact ALPACA parity),
// so there's one implementation, not a second. The only wrapper concern is
// z-index: this popup opens ABOVE the New-Contact modal (z 1100+), and the
// System-Management dialogs use `.acc-overlay` at z-index 1000 — so we scope a
// bump to `.srn-country-host .acc-overlay` (and the lookup-select popups) to sit
// above the Contact modal. Confirm/alert dialogs (z 9500, portaled to the app
// root) stay above everything, so the Unlink confirm still works.
// ─────────────────────────────────────────────────────────────────────────────

export function CountryDetailModal({ countryId, onClose, onSaved, notify }: {
  countryId: string | null;              // null = create new; else edit that country
  onClose: () => void;
  onSaved: (savedCountryId: string) => void;
  notify: NotifyApi;
}) {
  const runSave = useSaveFlow();
  const save = useSaveCountry();
  const qc = useQueryClient();
  const { data: countries = [] } = useCountries();
  const existing = useMemo<Country | null>(
    () => (countryId ? countries.find((c) => c.id === countryId) ?? null : null),
    [countryId, countries]);

  const [longName, setLongName] = useState(existing?.longName ?? '');
  const [shortName, setShortName] = useState(existing?.shortName ?? '');
  const [flag, setFlag] = useState<string | null>(existing?.nationalFlag ?? null);   // base64, no data-URI prefix
  const [err, setErr] = useState('');
  const [stagedStates, setStagedStates] = useState<StagedState[]>([]);
  const flagInputRef = useRef<HTMLInputElement>(null);
  const [maxed, setMaxed] = useState(false);

  const onFlagFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFlag(String(reader.result).split(',')[1] ?? null);
    reader.readAsDataURL(file);
  };

  // Save the country (create/update), commit any staged State→City→Area chain
  // (only for a brand-new country), then hand the saved id back to the caller.
  const doSave = async () => {
    if (!longName.trim()) { setErr('English Long Name must not be empty.'); return; }
    setErr('');
    const wasNew = !existing?.id;
    let newId: string | undefined;
    await runSave({
      save: async () => {
        const res = await save.mutateAsync({
          id: existing?.id, longName: longName.trim(), shortName: shortName.trim() || null,
          chineseLongName: existing?.chineseLongName ?? null, chineseShortName: existing?.chineseShortName ?? null,
          nationalFlag: flag,
        });
        newId = existing?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
        // Commit states staged on a new country → their cities → their areas
        // (the full nested chain), mirroring CountryPage's staged-save.
        if (wasNew && newId && stagedStates.length) {
          for (const ss of stagedStates) {
            let stateId = ss.stateId;
            if (stateId) {
              await api.put(`/api/v1/system/states/${stateId}`, { longName: ss.name, shortName: ss.shortName ?? null, countryId: newId });
            } else {
              const sres = await api.post('/api/v1/system/states', { longName: ss.name, shortName: ss.shortName ?? null, countryId: newId });
              stateId = (sres as { data?: { data?: { id?: string } } })?.data?.data?.id;
            }
            if (stateId && ss.cities?.length) {
              for (const sc of ss.cities) {
                let cityId = sc.cityId;
                if (cityId) {
                  await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId });
                } else {
                  const cres = await api.post('/api/v1/cities', { cityName: sc.name, stateId });
                  cityId = (cres as { data?: { data?: { id?: string } } })?.data?.data?.id;
                }
                if (cityId && sc.areas?.length) {
                  for (const a of sc.areas) {
                    if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                    else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
                  }
                }
              }
            }
            await qc.invalidateQueries({ queryKey: ['sm-states-full'] });
            await qc.invalidateQueries({ queryKey: ['sm-cities'] });
            await qc.invalidateQueries({ queryKey: ['sm-city-areas'] });
          }
        }
      },
      successMessage: 'Saved successfully.',
      onSaved: () => { if (newId) onSaved(newId); else onClose(); },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  return (
    <div className="srn-country-host">
      {/* Scoped z-index bump so this editor + its nested State/City/Area popups
          stack ABOVE the New-Contact modal (z 1100). Confirm dialogs (z 9500,
          portaled to root) stay on top. */}
      <style>{`
        .srn-country-host .acc-overlay { z-index: 3100; }
        .srn-country-host .acc-lksel-backdrop { z-index: 3200; }
        .srn-country-host .acc-lksel-pop { z-index: 3201; }
      `}</style>
      <div className="acc-overlay" onMouseDown={onClose}>
        <div className={`acc-dialog acc-dialog-md${maxed ? ' acc-dialog-max' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
          <div className="acc-dialog-hdr">
            <span>{existing ? `Country - ${existing.longName ?? ''}` : 'Country'}</span>
            <span className="acc-dialog-acts">
              <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
              <i className="ti ti-x acc-dialog-x" onClick={onClose} />
            </span>
          </div>
          <div className="acc-dialog-body">
            <div className="acc-field-h">
              <label className="acc-label acc-label-req">English Long Name<span className="req">*</span></label>
              <div className="acc-fh-control">
                <input className="acc-input" autoFocus value={longName} onChange={(e) => { setLongName(e.target.value); if (err) setErr(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void doSave(); }} />
                {err && <div className="acc-err">{err}</div>}
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">English Short Name</label>
              <div className="acc-fh-control">
                <input className="acc-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">National Flag</label>
              <div className="acc-fh-control">
                {flag ? (
                  <>
                    <img className="cty-flag-lg" src={`data:image;base64,${flag}`} alt="National flag preview" />
                    <div className="cty-flag-acts">
                      <button className="cty-flag-btn" title="Edit / choose image" onClick={() => flagInputRef.current?.click()}><i className="ti ti-pencil" /></button>
                      <button className="cty-flag-btn" title="Erase" onClick={() => setFlag(null)}><i className="ti ti-eraser" /></button>
                    </div>
                  </>
                ) : (
                  <div className="acc-lookup">
                    <input className="acc-input" readOnly value="" onClick={() => flagInputRef.current?.click()} />
                    <button className="acc-browse-btn" onClick={() => flagInputRef.current?.click()}>Browse…</button>
                  </div>
                )}
                <input ref={flagInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => { onFlagFile(e.target.files?.[0]); e.target.value = ''; }} />
              </div>
            </div>
            {/* The nested Country → State (→ City → Area) editor — full parity. */}
            <CountryStatesPanel countryId={existing?.id} staged={stagedStates} onStaged={setStagedStates} />
          </div>
          <div className="acc-dialog-ftr">
            <button className="ca-btn ca-btn-solid" onClick={() => void doSave()} disabled={save.isPending}><span className="tb-i"><i className="ti ti-device-floppy" /></span> OK</button>
            <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey"><i className="ti ti-arrow-back-up" /></span> Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
